import React from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import LinkIcon from "@mui/icons-material/Link";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import WarehouseIcon from "@mui/icons-material/Warehouse";

const COR = "#1a9e8f";
const CINZA = "#8e9bae";
const BORDA = "#dfe3e8";

// Datas vêm em ISO (UTC); o dia gravado é a parte da data, sem hora.
const formatDate = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "");
const kg = (n) => `${(n || 0).toLocaleString("pt-BR")} kg`;
const descreverOp = (o) => `OP #${o.numeroOP}${o.lote ? ` (lote ${o.lote})` : ""}`;

const cabecalho = { fontWeight: 600, color: CINZA, fontSize: "0.7rem", py: 0.75 };
const celula = { fontSize: "0.8rem", py: 0.75 };

// Um ponto da linha do tempo: ícone, título, data e o conteúdo abaixo.
function Marco({ icon, titulo, data, subtitulo, ultimo, children }) {
	return (
		<Box sx={{ display: "flex", gap: 1.5, pb: ultimo ? 0 : 2.5, position: "relative" }}>
			{!ultimo && <Box sx={{ position: "absolute", left: 13, top: 28, bottom: 0, width: 2, bgcolor: BORDA }} />}
			<Box sx={{ position: "relative", zIndex: 1, width: 28, height: 28, borderRadius: "50%", bgcolor: `${COR}15`, border: `1px solid ${COR}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
				{icon}
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
					<Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{titulo}</Typography>
					{data && <Typography variant="caption" sx={{ color: CINZA }}>{formatDate(data)}</Typography>}
				</Box>
				{subtitulo && <Typography variant="caption" sx={{ color: CINZA, display: "block" }}>{subtitulo}</Typography>}
				{children}
			</Box>
		</Box>
	);
}

function EtapasDaOp({ etapas }) {
	const usaProduto = etapas.some((e) => e.produtoUtilizado);
	return (
		<TableContainer sx={{ border: `1px solid ${BORDA}`, borderRadius: "8px", mt: 1 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell sx={cabecalho}>Etapa</TableCell>
						<TableCell sx={cabecalho} align="right">Entrada</TableCell>
						{usaProduto && <TableCell sx={cabecalho}>Produto de outra OP</TableCell>}
						<TableCell sx={cabecalho} align="right">Saída</TableCell>
						<TableCell sx={cabecalho} align="right">Perdas</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{etapas.map((e) => (
						<TableRow key={e.numero}>
							<TableCell sx={celula}>{e.numero}. {e.nome}</TableCell>
							<TableCell sx={celula} align="right">{kg(e.entradaKg)}</TableCell>
							{usaProduto && (
								<TableCell sx={celula}>
									{e.produtoUtilizado ? (
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<CallMergeIcon sx={{ fontSize: 14, color: COR }} />
											<span>
												+{kg(e.produtoUtilizado.quantidadeKg)} da <b>{descreverOp(e.produtoUtilizado)}</b>
												{e.produtoUtilizado.loteConsolidado && <span style={{ color: CINZA }}> · lote consolidado {e.produtoUtilizado.loteConsolidado}</span>}
											</span>
										</Box>
									) : (
										<span style={{ color: CINZA }}>—</span>
									)}
								</TableCell>
							)}
							<TableCell sx={{ ...celula, fontWeight: 500 }} align="right">{kg(e.saidaKg)}</TableCell>
							<TableCell sx={{ ...celula, color: e.perdasKg > 0 ? "#dc2626" : CINZA }} align="right">{kg(e.perdasKg)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

function Destino({ d }) {
	const venda = d.tipo === "venda";
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, border: `1px solid ${BORDA}`, borderRadius: "6px", bgcolor: "white", px: 1.25, py: 0.75 }}>
			{venda ? <LocalShippingIcon sx={{ fontSize: 16, color: CINZA }} /> : <CallSplitIcon sx={{ fontSize: 16, color: COR }} />}
			<Typography variant="caption" sx={{ color: CINZA, minWidth: 70 }}>{formatDate(d.data)}</Typography>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				{venda ? (
					<Typography variant="caption" sx={{ display: "block" }}>
						Venda para <b>{d.cliente}</b>
						{d.cidade && <span style={{ color: CINZA }}> ({d.cidade})</span>}
						{d.notaFiscal && <span style={{ color: CINZA }}> · NF {d.notaFiscal}</span>}
					</Typography>
				) : (
					<Typography variant="caption" sx={{ display: "block" }}>
						Utilizado na <b>{descreverOp(d)}</b>, etapa "{d.etapa}" — {d.produto}
						{d.loteConsolidado && <span style={{ color: CINZA }}> · lote consolidado {d.loteConsolidado}</span>}
					</Typography>
				)}
			</Box>
			<Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{kg(d.quantidadeKg)}</Typography>
		</Box>
	);
}

// Linha do tempo do lote: recebimento, consolidação e, para cada OP, as etapas
// e para onde foi o produto resultante (vendas e uso em outras OPs).
function HistoricoLote({ lote }) {
	const ordens = lote.ordens ?? [];
	const fornecedores = new Set(lote.lotesOrigem.map((lo) => lo.fornecedor)).size;

	return (
		<Box sx={{ pl: 1 }}>
			<Marco
				icon={<InventoryIcon sx={{ fontSize: 15, color: COR }} />}
				titulo="Recebimento"
				data={lote.lotesOrigem.map((lo) => lo.dataEntrada).sort()[0]}
				subtitulo={`${lote.lotesOrigem.length} lote(s) de origem de ${fornecedores} fornecedor(es)`}
			/>
			<Marco
				icon={<LinkIcon sx={{ fontSize: 15, color: COR }} />}
				titulo={`Consolidação no lote ${lote.loteConsolidado}`}
				data={lote.dataConsolidacao}
				subtitulo={kg(lote.pesoTotalKg)}
				ultimo={ordens.length === 0}
			/>
			{ordens.map((op, idx) => (
				<Marco
					key={op.numeroOP}
					icon={<PrecisionManufacturingIcon sx={{ fontSize: 15, color: COR }} />}
					titulo={`OP #${op.numeroOP}${op.processo ? ` · ${op.processo}` : ""}`}
					data={op.data}
					subtitulo={`Entrada de ${kg(op.quantidadeEntradaKg)} do lote → ${op.produtoFinal}${op.lote ? ` · lote ${op.lote}` : ""}`}
					ultimo={idx === ordens.length - 1}
				>
					<EtapasDaOp etapas={op.etapas} />

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
						<Typography variant="caption">Produto resultante: <b>{kg(op.quantidadeFinalKg)}</b></Typography>
						<Typography variant="caption" sx={{ color: "#dc2626" }}>Perdas: {kg(op.perdasKg)}</Typography>
					</Box>

					<Typography variant="caption" sx={{ display: "block", mt: 1.5, mb: 0.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: CINZA, fontSize: "0.6rem" }}>
						Destino do produto resultante
					</Typography>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
						{op.destinos.map((d, i) => <Destino key={`${d.tipo}-${i}`} d={d} />)}
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, border: `1px dashed ${BORDA}`, borderRadius: "6px", px: 1.25, py: 0.75 }}>
							<WarehouseIcon sx={{ fontSize: 16, color: CINZA }} />
							<Typography variant="caption" sx={{ flex: 1, color: CINZA }}>
								{op.destinos.length === 0 ? "Nenhuma venda ou uso em outra OP — saldo em estoque" : "Saldo em estoque"}
							</Typography>
							<Typography variant="caption" sx={{ fontWeight: 600 }}>{kg(op.saldoKg)}</Typography>
						</Box>
					</Box>
				</Marco>
			))}
		</Box>
	);
}

export default HistoricoLote;
